import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Phone as PhoneIcon, EnvelopeSimple, MapPin, WifiHigh, Clock, Plus, Check, Hourglass, NavigationArrow, MapTrifold, X, Crosshair } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// ─── Location Picker Modal ───────────────────────────────────────────────────
function LocationPickerModal({ username, currentLat, currentLng, onSaved, onClose }) {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markerRef = useRef(null);
  const [lat, setLat] = useState(currentLat || '');
  const [lng, setLng] = useState(currentLng || '');
  const [saving, setSaving] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  // Initialize Leaflet map
  useEffect(() => {
    if (!mapRef.current || leafletMapRef.current) return;
    // Load Leaflet CSS if not loaded
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    const initMap = () => {
      if (!window.L || leafletMapRef.current) return;
      const L = window.L;
      const defaultLat = currentLat && currentLat !== 0 ? parseFloat(currentLat) : 16.8409;
      const defaultLng = currentLng && currentLng !== 0 ? parseFloat(currentLng) : 96.1235;
      const map = L.map(mapRef.current, { zoomControl: true }).setView([defaultLat, defaultLng], 16);
      // Google Satellite layer
      L.tileLayer('https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
        attribution: '© Google', maxZoom: 21
      }).addTo(map);
      // Marker
      const marker = L.marker([defaultLat, defaultLng], { draggable: true }).addTo(map);
      marker.on('dragend', (e) => {
        const pos = e.target.getLatLng();
        setLat(pos.lat.toFixed(7));
        setLng(pos.lng.toFixed(7));
      });
      map.on('click', (e) => {
        marker.setLatLng(e.latlng);
        setLat(e.latlng.lat.toFixed(7));
        setLng(e.latlng.lng.toFixed(7));
      });
      leafletMapRef.current = map;
      markerRef.current = marker;
      if (currentLat && currentLat !== 0) {
        setLat(parseFloat(currentLat).toFixed(7));
        setLng(parseFloat(currentLng).toFixed(7));
      }
      setMapReady(true);
    };
    if (window.L) {
      initMap();
    } else {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initMap;
      document.head.appendChild(script);
    }
    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        markerRef.current = null;
      }
    };
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    navigator.geolocation.getCurrentPosition((pos) => {
      const { latitude, longitude } = pos.coords;
      setLat(latitude.toFixed(7));
      setLng(longitude.toFixed(7));
      if (leafletMapRef.current && markerRef.current) {
        leafletMapRef.current.setView([latitude, longitude], 18);
        markerRef.current.setLatLng([latitude, longitude]);
      }
      toast.success('Location detected!');
    }, () => toast.error('Could not get your location'));
  };

  const jumpToCoords = () => {
    const la = parseFloat(lat), ln = parseFloat(lng);
    if (isNaN(la) || isNaN(ln)) { toast.error('Invalid coordinates'); return; }
    if (leafletMapRef.current && markerRef.current) {
      leafletMapRef.current.setView([la, ln], 18);
      markerRef.current.setLatLng([la, ln]);
    }
  };

  const save = async () => {
    const la = parseFloat(lat), ln = parseFloat(lng);
    if (isNaN(la) || isNaN(ln)) { toast.error('Please select a valid location'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/customers/${username}/location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat: la, lng: ln })
      });
      if (res.ok) {
        toast.success('✅ Location saved successfully!');
        onSaved(la, ln);
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save location');
      }
    } catch { toast.error('Network error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900">📍 Set Customer Location</h3>
            <p className="text-xs text-gray-500 mt-0.5">Click on map or drag the marker to set location for <span className="font-semibold text-blue-600">{username}</span></p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500"><X size={18} /></button>
        </div>
        {/* Map */}
        <div className="relative flex-1" style={{ minHeight: 320 }}>
          <div ref={mapRef} style={{ width: '100%', height: '100%', minHeight: 320 }} />
          {!mapReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="w-8 h-8 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        {/* Controls */}
        <div className="px-5 py-4 border-t border-gray-100 space-y-3">
          {/* Coordinate inputs */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Latitude</label>
              <input
                type="number" step="any" value={lat} onChange={e => setLat(e.target.value)}
                placeholder="e.g. 16.8409"
                className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Longitude</label>
              <input
                type="number" step="any" value={lng} onChange={e => setLng(e.target.value)}
                placeholder="e.g. 96.1235"
                className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              <button onClick={jumpToCoords} className="mb-0.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-700 flex items-center gap-1.5">
                <Crosshair size={14} /> Go
              </button>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex gap-2">
            <button onClick={useMyLocation} className="flex items-center gap-1.5 px-3 py-2 border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
              <NavigationArrow size={14} /> Use My Location
            </button>
            <div className="flex-1" />
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving || !lat || !lng} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold disabled:opacity-50 flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check size={14} />}
              {saving ? 'Saving...' : 'Save Location'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main CustomerProfile ────────────────────────────────────────────────────
export default function CustomerProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [ticketHistory, setTicketHistory] = useState({ rows: [], open: [], history: [] });
  const [ticketHistoryLoading, setTicketHistoryLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callOpen, setCallOpen] = useState(false);
  const [callForm, setCallForm] = useState({ call_reason: '', call_status: 'pending', notes: '' });
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewForm, setRenewForm] = useState({ invoice_id: '', months: 1, payment_amount: '', payment_method: 'KBZPay', remark: '' });
  const [locationOpen, setLocationOpen] = useState(false);
  const [customerLat, setCustomerLat] = useState(null);
  const [customerLng, setCustomerLng] = useState(null);
  const [networkHealth, setNetworkHealth] = useState(null);
  const [networkHistory, setNetworkHistory] = useState([]);

  const load = async () => {
    setLoading(true);
    setTicketHistoryLoading(true);
    try {
      const [custRes, callRes, ticketRes, locRes] = await Promise.all([
        fetch(`${API}/customers/${username}`),
        fetch(`${API}/call-history/${username}`),
        fetch(`${API}/customers/${username}/ticket-history`),
        fetch(`${API}/customers/${username}/location`)
      ]);
      if (custRes.ok) setData(await custRes.json());
      else { toast.error('Customer not found'); navigate('/customers'); return; }
      if (callRes.ok) { const _d = await callRes.json(); setCallHistory(Array.isArray(_d) ? _d : []); }
      if (ticketRes.ok) setTicketHistory(await ticketRes.json());
      if (locRes.ok) {
        const locData = await locRes.json();
        if (locData.has_location) {
          setCustomerLat(locData.lat);
          setCustomerLng(locData.lng);
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); setTicketHistoryLoading(false); }
  };
  useEffect(() => { load(); }, [username]);
  // Poll real accounting data only. Unavailable samples are never turned into fake points.
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/customers/${username}/network-health`);
        if (!res.ok || !alive) return;
        const next = await res.json();
        if (!alive) return;
        setNetworkHealth(next);
        if (next.available && Number.isFinite(Number(next.download_mbps)) && Number.isFinite(Number(next.upload_mbps))) {
          setNetworkHistory(prev => [...prev, { at: next.sample_at || Date.now() / 1000, dl: Number(next.download_mbps), ul: Number(next.upload_mbps) }].slice(-30));
        }
      } catch (err) { if (alive) setNetworkHealth({ available: false, reason: 'Network accounting unavailable' }); }
    };
    poll();
    const timer = setInterval(poll, 10000);
    return () => { alive = false; clearInterval(timer); };
  }, [username]);

  const submitCall = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/call-history`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, ...callForm }) });
      if (res.ok) { toast.success('Call logged'); setCallOpen(false); setCallForm({ call_reason: '', call_status: 'pending', notes: '' }); load(); }
    } catch { toast.error('Failed'); }
  };
  const updateCallStatus = async (id, status) => {
    await fetch(`${API}/call-history/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ call_status: status }) });
    toast.success('Status updated'); load();
  };
  const submitRenew = async (e) => {
    e.preventDefault();
    if (!renewForm.invoice_id) { toast.error('Select an Unpaid invoice first'); return; }
    setRenewLoading(true);
    try {
      const res = await fetch(`${API}/finance/renew-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, invoice_id: Number(renewForm.invoice_id), months: 1, payment_amount: Number(renewForm.payment_amount), payment_method: renewForm.payment_method, remark: renewForm.remark })
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(`✅ Renewed! New expiry: ${result.new_expiration}`);
        setRenewOpen(false);
        load();
      } else {
        toast.error(result.detail || 'Renewal failed');
      }
    } catch (err) { toast.error('Renewal failed'); }
    finally { setRenewLoading(false); }
  };

  const viewOnMap = () => {
    if (customerLat && customerLng) {
      navigate(`/fiber-map?focus=${customerLat},${customerLng}&username=${username}`);
    } else {
      toast.info('No location set. Please set location first.');
      setLocationOpen(true);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-[3px] border-[var(--md-primary)] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;
  const u = data.user || {};
  const linkedNetwork = data.linked_network || {};
  const linkedOnu = (linkedNetwork.onu_devices || [])[0] || {};
  const isActive = u.enableuser === 1;
  const formatBytes = (b) => { if (!b) return '0 B'; const k = 1024; const s = ['B','KB','MB','GB','TB']; const i = Math.floor(Math.log(Math.abs(b)||1)/Math.log(k)); return `${(b/Math.pow(k,i)).toFixed(1)} ${s[i]}`; };
  const hasLocation = !!(customerLat && customerLng);

  return (
    <div className="space-y-4" data-testid="customer-profile">
      {/* Location Picker Modal */}
      {locationOpen && (
        <LocationPickerModal
          username={username}
          currentLat={customerLat}
          currentLng={customerLng}
          onSaved={(la, ln) => { setCustomerLat(la); setCustomerLng(ln); }}
          onClose={() => setLocationOpen(false)}
        />
      )}

      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/customers')} className="p-2 rounded-full hover:bg-[var(--md-surface-2)]"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h2 className="text-lg font-bold">{u.firstname} {u.lastname}</h2>
          <p className="text-xs text-[var(--md-on-surface-variant)] tabular-nums">{u.username}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <span className={`md3-chip text-xs ${isActive ? 'badge-completed' : 'badge-rejected'}`}>{isActive ? 'Active' : 'Expired'}</span>
          {/* Location buttons */}
          <button
            onClick={() => setLocationOpen(true)}
            className={`flex items-center gap-1.5 text-xs font-medium py-1.5 px-3 rounded-lg border transition-colors ${hasLocation ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100' : 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
            title={hasLocation ? `Location set: ${customerLat?.toFixed(4)}, ${customerLng?.toFixed(4)}` : 'No location set'}
          >
            <MapPin size={13} />
            {hasLocation ? 'Update Location' : 'Set Location'}
          </button>
          {hasLocation && (
            <button
              onClick={viewOnMap}
              className="flex items-center gap-1.5 text-xs font-medium py-1.5 px-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            >
              <MapTrifold size={13} /> View on Map
            </button>
          )}
          {!isActive && (
            <button onClick={() => {
              const unpaid = (data.invoices || []).filter(inv => !inv.paid || inv.paid === '0000-00-00' || inv.paid === '0000-00-00 00:00:00');
              if (!unpaid.length) { toast.info('No Unpaid invoice is available for this customer'); return; }
              const invoice = unpaid[0];
              setRenewForm({ invoice_id: String(invoice.id), months: 1, payment_amount: String(invoice.price || invoice.amount || ''), payment_method: 'KBZPay', remark: '' });
              setRenewOpen(true);
            }}
              className="flex items-center gap-1.5 bg-[#006D3B] text-white text-xs font-bold py-1.5 px-3 rounded-lg hover:bg-[#005A30] transition-colors shadow-sm">
              🔄 Renew Subscription
            </button>
          )}
        </div>
      </div>

      {/* Renew Dialog */}
      {renewOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-base font-bold mb-1">🔄 Renew Subscription</h3>
            <p className="text-xs text-[var(--md-on-surface-variant)] mb-4">Renewing: <span className="font-bold text-[var(--md-primary)]">{username}</span></p>
            <form onSubmit={submitRenew} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[var(--md-on-surface-variant)]">Unpaid Invoice</label>
                <select value={renewForm.invoice_id} onChange={e => { const invoice = (data.invoices || []).find(inv => String(inv.id) === e.target.value); setRenewForm({...renewForm, invoice_id: e.target.value, payment_amount: String(invoice?.price || invoice?.amount || '')}); }} className="w-full mt-1 border border-[var(--md-outline)] rounded-lg px-3 py-2 text-sm bg-white" required>
                  {(data.invoices || []).filter(inv => !inv.paid || inv.paid === '0000-00-00' || inv.paid === '0000-00-00 00:00:00').map(inv => <option key={inv.id} value={inv.id}>{inv.invnum || `Invoice #${inv.id}`} — {Number(inv.price || inv.amount || 0).toLocaleString()} MMK</option>)}
                </select>
                <p className="text-[11px] text-[var(--md-on-surface-variant)] mt-1">Staff can pay an existing Unpaid invoice only.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-[var(--md-on-surface-variant)]">Invoice Amount (MMK)</label>
                <input type="number" value={renewForm.payment_amount} readOnly required className="w-full mt-1 border border-[var(--md-outline)] rounded-lg px-3 py-2 text-sm bg-gray-50" />
              </div>
              <div>
                <label className="text-xs font-medium text-[var(--md-on-surface-variant)]">Payment Method</label>
                <select value={renewForm.payment_method} onChange={e => setRenewForm({...renewForm, payment_method: e.target.value})} className="w-full mt-1 border border-[var(--md-outline)] rounded-lg px-3 py-2 text-sm bg-white">
                  {['KBZPay','Wave Money','AYA Pay','CB Pay','Cash','Bank Transfer','Other'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[var(--md-on-surface-variant)]">Remark</label>
                <input type="text" value={renewForm.remark} onChange={e => setRenewForm({...renewForm, remark: e.target.value})} className="w-full mt-1 border border-[var(--md-outline)] rounded-lg px-3 py-2 text-sm" placeholder="e.g. bill done" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setRenewOpen(false)} className="flex-1 border border-[var(--md-outline)] rounded-lg py-2 text-sm font-medium hover:bg-[var(--md-surface-1)]">Cancel</button>
                <button type="submit" disabled={renewLoading} className="flex-1 bg-[#006D3B] text-white rounded-lg py-2 text-sm font-bold hover:bg-[#005A30] disabled:opacity-60">
                  {renewLoading ? 'Renewing...' : 'Confirm Renew'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md3-card p-4">
          <h4 className="text-xs font-bold uppercase text-[var(--md-primary)] mb-3">Contact Info</h4>
          <div className="space-y-2 text-sm">
            <p className="flex items-center gap-2"><PhoneIcon size={14} className="text-[var(--md-on-surface-variant)]" /><span className="text-[var(--md-on-surface-variant)]">Customer Phone Number:</span><b>{u.phone || u.mobile || '—'}</b></p>
            <p className="flex items-start gap-2"><MapPin size={14} className="text-[var(--md-on-surface-variant)] mt-0.5" /><span><span className="text-[var(--md-on-surface-variant)]">Customer Address:</span> <b>{[u.address, u.city].filter(Boolean).join(', ') || '—'}</b></span></p>
            <p className="flex items-center gap-2"><NavigationArrow size={14} className="text-[var(--md-on-surface-variant)]" /><span className="text-[var(--md-on-surface-variant)]">Customer Location:</span><b className="font-mono">{hasLocation ? `${parseFloat(customerLat).toFixed(5)}, ${parseFloat(customerLng).toFixed(5)}` : (u.gpslat && u.gpslong ? `${Number(u.gpslat).toFixed(5)}, ${Number(u.gpslong).toFixed(5)}` : '—')}</b></p>
            {u.email && <p className="flex items-center gap-2"><EnvelopeSimple size={14} className="text-[var(--md-on-surface-variant)]" />{u.email}</p>}
            {/* GPS Location row */}
            <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
              <NavigationArrow size={14} className={hasLocation ? 'text-green-600' : 'text-gray-400'} />
              {hasLocation ? (
                <span className="text-xs font-mono text-green-700">{parseFloat(customerLat).toFixed(5)}, {parseFloat(customerLng).toFixed(5)}</span>
              ) : (
                <span className="text-xs text-gray-400 italic">No GPS location set</span>
              )}
              <button onClick={() => setLocationOpen(true)} className="ml-auto text-[10px] text-blue-600 hover:underline font-medium">
                {hasLocation ? 'Edit' : 'Set'}
              </button>
            </div>
          </div>
        </div>
        <div className="md3-card p-4">
          <h4 className="text-xs font-bold uppercase text-[var(--md-primary)] mb-3">Service Details</h4>
          <div className="space-y-2 text-sm">
            <p><span className="text-[var(--md-on-surface-variant)]">Customer Plan:</span> <span className="font-medium">{data.marketing?.customer_plan || data.service_name || u.srvname || '—'}</span></p>
            <p><span className="text-[var(--md-on-surface-variant)]">Speed:</span> <span className="font-medium tabular-nums">{u.downrate ? `${u.downrate}/${u.uprate}` : '-'}</span></p>
            <p><span className="text-[var(--md-on-surface-variant)]">Expiration:</span> <span className="font-medium">{u.expiration || '-'}</span></p>
            <p><span className="text-[var(--md-on-surface-variant)]">Type:</span> <span className="font-medium">{u.acctype || '-'}</span></p>
            <p><span className="text-[var(--md-on-surface-variant)]">Amount:</span> <span className="font-medium tabular-nums text-green-600">{data.monthly_fee ? (data.invoices && data.invoices[0] && data.invoices[0].issuedate && data.invoices[0].issuedate >= "2026-05-04" ? Math.round(Number(data.monthly_fee) * 1.1).toLocaleString() : Number(data.monthly_fee).toLocaleString()) + ' MMK' : '-'}</span></p>
          </div>
        </div>
        <div className="md3-card p-4">
          <h4 className="text-xs font-bold uppercase text-[var(--md-primary)] mb-3">Network Info</h4>
          <div className="mb-3 rounded-xl bg-[var(--md-surface-1)] p-3 text-xs">
            <div className="flex items-center justify-between mb-2"><b>PPPoE ↔ IP ↔ ONU Link</b><span className={`md3-chip text-[10px] ${linkedNetwork.session_status === 'Online' ? 'badge-completed' : 'badge-pending'}`}>{linkedNetwork.session_status || 'Unknown'}</span></div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1"><span>PPPoE: <b>{linkedNetwork.pppoe_account || u.username || '—'}</b></span><span>CPE IP: <b className="font-mono">{linkedNetwork.cpe_ip || linkedNetwork.current_ip || '—'}</b></span><span>ONU S/N: <b>{linkedNetwork.onu_serial || linkedOnu.serial_number || '—'}</b></span><span>MAC: <b className="font-mono">{linkedNetwork.mac || linkedOnu.mac || '—'}</b></span><span>ONU: <b>{linkedNetwork.onu_model || linkedOnu.model || '—'}</b></span><span>OLT: <b>{linkedNetwork.olt_name || linkedOnu.olt_name || '—'}</b></span><span>PON: <b>{linkedNetwork.pon_port || linkedOnu.pon_port || '—'}</b></span></div>
            <div className="mt-2 text-[10px] text-[var(--md-on-surface-variant)]">Linked ONU records: {linkedNetwork.onu_count || 0} · Source: RADIUS session + ERP ONU DB</div>
          </div>
          <div className="space-y-2 text-sm">
            <p><span className="text-[var(--md-on-surface-variant)]">MAC:</span> <span className="font-medium tabular-nums">{u.mac || u.maccm || '-'}</span></p>
            <p><span className="text-[var(--md-on-surface-variant)]">CPE IP:</span> <span className="font-medium tabular-nums">{u.staticipcpe || '-'}</span></p>
            <p><span className="text-[var(--md-on-surface-variant)]">CM IP:</span> <span className="font-medium tabular-nums">{u.staticipcm || '-'}</span></p>
            <p><span className="text-[var(--md-on-surface-variant)]">Last Logoff:</span> <span className="font-medium">{u.lastlogoff || '-'}</span></p>
          </div>
        </div>
      </div>

      {/* Network & Live Health — real accounting data only */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="md3-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div><h4 className="text-xs font-bold uppercase text-[var(--md-primary)]">Network &amp; Live Health</h4><p className="text-[10px] text-[var(--md-on-surface-variant)]">Real customer accounting · auto update 10s</p></div>
            <span className={`md3-chip text-[10px] ${networkHealth?.available ? 'badge-completed' : 'badge-pending'}`}>{networkHealth?.available ? 'Data available' : 'No live data'}</span>
          </div>
          {networkHistory.length < 2 ? (
            <div className="h-40 flex items-center justify-center rounded-xl bg-[var(--md-surface-1)] text-xs text-[var(--md-on-surface-variant)] text-center px-4">Real-time data မရှိသေးပါ။<br />Accounting sample နှစ်ကြိမ်ရမှ graph ပြပါမည်။</div>
          ) : (() => {
            const width = 620, height = 160, pad = 24;
            const max = Math.max(1, ...networkHistory.map(p => Math.max(p.dl, p.ul)));
            const point = (v, i) => `${pad + (i * (width - pad * 2)) / (networkHistory.length - 1)},${height - pad - (v / max) * (height - pad * 2)}`;
            return <div className="rounded-xl bg-[var(--md-surface-1)] p-2"><div className="flex items-center gap-4 text-[10px] mb-1"><span className="font-bold text-blue-600">━ DL Mbps</span><span className="font-bold text-green-600">━ UL Mbps</span><span className="ml-auto text-[var(--md-on-surface-variant)]">max {max.toFixed(2)} Mbps</span></div><svg viewBox={`0 0 ${width} ${height}`} className="w-full h-36" role="img" aria-label="Download and upload speed graph"><polyline fill="none" stroke="#2563eb" strokeWidth="3" points={networkHistory.map((p, i) => point(p.dl, i)).join(' ')} /><polyline fill="none" stroke="#16a34a" strokeWidth="3" points={networkHistory.map((p, i) => point(p.ul, i)).join(' ')} /></svg><div className="text-[10px] text-[var(--md-on-surface-variant)]">Source: {networkHealth?.source || 'RADIUS accounting'} · Last sample: {networkHealth?.sample_at ? new Date(networkHealth.sample_at * 1000).toLocaleTimeString() : '-'}</div></div>;
          })()}
          {!networkHealth?.available && networkHealth?.reason && <p className="text-[10px] text-[var(--md-on-surface-variant)] mt-2">{networkHealth.reason}</p>}
        </div>
        <div className="md3-card p-4">
          <div className="flex items-center justify-between mb-3"><div><h4 className="text-xs font-bold uppercase text-[var(--md-primary)]">Hardware / CPE</h4><p className="text-[10px] text-[var(--md-on-surface-variant)]">Existing customer–ONU/OLT records</p></div><span className="md3-chip text-[10px]">{(data.cpe_devices || []).length} device(s)</span></div>
          {(data.cpe_devices || []).length === 0 ? <div className="h-40 flex items-center justify-center rounded-xl bg-[var(--md-surface-1)] text-xs text-[var(--md-on-surface-variant)] text-center">ဒီ account နှင့်ချိတ်ထားသော CPE/ONU data မတွေ့ပါ။<br />OLT live call မလုပ်ထားပါ။</div> : <div className="space-y-2">{data.cpe_devices.map((cpe, i) => <div key={cpe.id || i} className="rounded-xl bg-[var(--md-surface-1)] p-3 text-xs"><div className="flex items-center justify-between mb-2"><b>{cpe.model || cpe.vendor || 'ONU/CPE'}</b><span className={`md3-chip text-[10px] ${String(cpe.status || '').toLowerCase() === 'online' ? 'badge-completed' : 'badge-pending'}`}>{cpe.status || 'Unknown'}</span></div><div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1"><span>Engineer Name: <b>{cpe.engineer_name || data.bind_details?.engineer_name || '—'}</b></span><span>FAT Name: <b>{cpe.fat_name || data.bind_details?.fat_name || '—'}</b></span><span>Use Port No: <b>{cpe.use_port_no || data.bind_details?.use_port_no || '—'}</b></span><span>ONU S/N: <b>{cpe.onu_sn || cpe.onu_serial || data.bind_details?.onu_sn || '—'}</b></span><span>Fiber Meter: <b>{cpe.fiber_meter || data.bind_details?.fiber_meter ? `${cpe.fiber_meter || data.bind_details?.fiber_meter} m` : '—'}</b></span><span>MAC: <b>{cpe.mac_address || u.mac || u.maccm || '—'}</b></span><span>OLT: <b>{cpe.olt_name || cpe.olt_id || '—'}</b></span><span>PON Port: <b>{cpe.frame_slot_port || [cpe.slot_no, cpe.port_no].filter(v => v !== null && v !== undefined && v !== '').join('/') || '—'}</b></span><span>Manufacturer: <b>{cpe.vendor || '—'}</b></span><span>Firmware: <b>{cpe.sw_version || '—'}</b></span></div></div>)}</div>}
        </div>
      </div>
      {/* Tabs: Call History, Sessions, Invoices */}
      <Tabs defaultValue="calls">
        <TabsList className="bg-white border border-[var(--md-outline-variant)] rounded-xl p-1">
          <TabsTrigger value="calls" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">Call History ({callHistory.length})</TabsTrigger>
          <TabsTrigger value="tickets" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">Ticket History ({ticketHistory.rows.length})</TabsTrigger>
          <TabsTrigger value="sessions" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">Sessions ({data.sessions?.length || 0})</TabsTrigger>
          <TabsTrigger value="invoices" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">Invoices ({data.invoices?.length || 0})</TabsTrigger>
        </TabsList>
        {/* Call History */}
        <TabsContent value="calls" className="space-y-3 mt-4">
          <div className="flex justify-end">
            <Dialog open={callOpen} onOpenChange={setCallOpen}>
              <DialogTrigger asChild><button className="md3-filled-btn text-xs flex items-center gap-1" data-testid="add-call-btn"><Plus size={14} /> Log Call</button></DialogTrigger>
              <DialogContent className="sm:max-w-md bg-white rounded-2xl">
                <DialogHeader><DialogTitle>Log Call</DialogTitle></DialogHeader>
                <form onSubmit={submitCall} className="space-y-3">
                  <div>
                    <label className="text-xs font-medium">Call Reason</label>
                    <input value={callForm.call_reason} onChange={e => setCallForm({...callForm, call_reason: e.target.value})} required className="w-full mt-1 border border-[var(--md-outline)] rounded-lg px-3 py-2 text-sm" placeholder="e.g. Billing inquiry" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Status</label>
                    <Select value={callForm.call_status} onValueChange={v => setCallForm({...callForm, call_status: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="escalated">Escalated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Notes</label>
                    <textarea value={callForm.notes} onChange={e => setCallForm({...callForm, notes: e.target.value})} rows={3} className="w-full mt-1 border border-[var(--md-outline)] rounded-lg px-3 py-2 text-sm resize-none" placeholder="Call notes..." />
                  </div>
                  <button type="submit" className="w-full md3-filled-btn">Save</button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {callHistory.length === 0 ? (
            <div className="md3-card p-8 text-center text-sm text-[var(--md-on-surface-variant)]">No call history</div>
          ) : (
            <div className="space-y-2">
              {callHistory.map((c, i) => (
                <div key={i} className="md3-card p-4 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--md-primary-container)] flex items-center justify-center flex-shrink-0"><PhoneIcon size={14} className="text-[var(--md-primary)]" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{c.call_reason}</span>
                      <span className={`md3-chip text-[10px] ${c.call_status === 'resolved' ? 'badge-completed' : c.call_status === 'escalated' ? 'badge-rejected' : 'badge-pending'}`}>{c.call_status}</span>
                    </div>
                    {c.notes && <p className="text-xs text-[var(--md-on-surface-variant)] mt-1">{c.notes}</p>}
                    <p className="text-[10px] text-[var(--md-on-surface-variant)] mt-1 flex items-center gap-1"><Clock size={10} />{c.created_at ? new Date(c.created_at).toLocaleString() : ''} · {c.logged_by || ''}</p>
                  </div>
                  {c.call_status !== 'resolved' && (
                    <button onClick={() => updateCallStatus(c._id || c.id, 'resolved')} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600" title="Mark resolved"><Check size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        {/* Ticket History */}
        <TabsContent value="tickets" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="md3-card p-4"><p className="text-[10px] uppercase font-bold text-[var(--md-on-surface-variant)]">လက်ရှိဖွင့်ထားသော Ticket</p><p className="text-2xl font-bold text-orange-600 mt-1">{ticketHistory.open.length}</p></div>
            <div className="md3-card p-4"><p className="text-[10px] uppercase font-bold text-[var(--md-on-surface-variant)]">Ticket History</p><p className="text-2xl font-bold text-[var(--md-primary)] mt-1">{ticketHistory.history.length}</p></div>
          </div>
          {ticketHistoryLoading ? <div className="md3-card p-8 text-center text-sm">Loading ticket history...</div> : ticketHistory.rows.length === 0 ? <div className="md3-card p-8 text-center text-sm text-[var(--md-on-surface-variant)]">Ticket history မရှိသေးပါ</div> : (
            <div className="md3-card overflow-hidden">
              <div className="px-4 py-3 border-b bg-[var(--md-surface-1)]"><h3 className="text-sm font-bold">Ticket History — နောက်ဆုံးဖွင့်/ပြင်ထားသည့်နေ့အလိုက်</h3></div>
              <div className="divide-y">
                {ticketHistory.rows.map((ticket, i) => <button key={ticket.ticket_id || i} onClick={() => setSelectedTicket(ticket)} className="w-full text-left px-4 py-3 hover:bg-[var(--md-surface-1)] transition-colors">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-mono text-xs font-bold text-[var(--md-primary)]">{ticket.ticket_id || '-'}</span><span className={`md3-chip text-[10px] ${ticket.is_open ? 'badge-pending' : 'badge-completed'}`}>{ticket.is_open ? 'Open / လက်ရှိ' : 'History / ပြီးဆုံး'}</span><span className="md3-chip text-[10px]">{ticket.status || '-'}</span><span className="ml-auto text-[10px] text-[var(--md-on-surface-variant)]">{ticket.last_activity_at ? new Date(ticket.last_activity_at).toLocaleString() : ticket.service_date || '-'}</span></div>
                  <div className="mt-1 text-xs"><strong>{ticket.issue_type || ticket.service_type || 'Service Ticket'}</strong> · {ticket.priority || 'Normal'} · {ticket.assigned_name || ticket.assigned_tech_name || 'မသတ်မှတ်ရသေး'}</div>
                  <div className="mt-1 text-[11px] text-[var(--md-on-surface-variant)] line-clamp-2">{ticket.description || ticket.work_notes || 'အသေးစိတ်ကြည့်ရန် နှိပ်ပါ'}</div>
                </button>)}
              </div>
            </div>
          )}
        </TabsContent>
        {selectedTicket && <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) setSelectedTicket(null); }}><div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto"><div className="flex items-start justify-between border-b pb-3 mb-4"><div><h3 className="text-base font-bold">Ticket Detail — {selectedTicket.ticket_id}</h3><p className="text-xs text-[var(--md-on-surface-variant)]">Customer ticket အသေးစိတ်</p></div><button onClick={() => setSelectedTicket(null)} className="text-xl font-bold">✕</button></div><div className="grid grid-cols-2 gap-3 text-xs"><div><b>Status:</b> {selectedTicket.status || '-'}</div><div><b>Priority:</b> {selectedTicket.priority || '-'}</div><div><b>Created:</b> {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : '-'}</div><div><b>Last activity:</b> {selectedTicket.last_activity_at ? new Date(selectedTicket.last_activity_at).toLocaleString() : '-'}</div><div><b>Service date:</b> {selectedTicket.service_date || '-'}</div><div><b>Assigned Team:</b> {selectedTicket.assigned_team || selectedTicket.assigned_name || selectedTicket.assigned_tech_name || '-'}</div><div><b>Created By Agent:</b> {selectedTicket.created_by_agent || selectedTicket.created_by_name || selectedTicket.created_by || '-'}</div><div><b>Agent Remark:</b> {selectedTicket.agent_remark || '-'}</div><div><b>Transfer Reason:</b> {selectedTicket.transfer_reason || '-'}</div></div><div className="mt-4 rounded-xl bg-[var(--md-surface-1)] p-3 text-xs whitespace-pre-wrap"><b>Issue:</b> {selectedTicket.issue_type || selectedTicket.service_type || '-'}\n\n<b>Description:</b> {selectedTicket.description || '-'}\n\n<b>Work notes:</b> {selectedTicket.work_notes || '-'}\n\n<b>Completion:</b> {selectedTicket.completion_time || '-'}</div></div></div>}
        {/* Sessions */}
        <TabsContent value="sessions" className="mt-4">
          <div className="md3-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-[var(--md-surface-1)]">
                  <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Session ID</th>
                  <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden md:table-cell">NAS IP</th>
                  <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Framed IP</th>
                  <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Start</th>
                  <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden md:table-cell">Stop</th>
                  <th className="text-right text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Download</th>
                  <th className="text-right text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Upload</th>
                </tr></thead>
                <tbody>
                  {(data.sessions || []).length === 0 ? <tr><td colSpan={7} className="text-center py-6 text-sm text-[var(--md-on-surface-variant)]">No sessions</td></tr> :
                  (data.sessions || []).map((s, i) => (
                    <tr key={i} className="border-t border-[var(--md-outline-variant)] md3-table-row">
                      <td className="px-4 py-2 text-[10px] tabular-nums">{s.acctsessionid}</td>
                      <td className="px-4 py-2 text-[10px] tabular-nums hidden md:table-cell">{s.nasipaddress}</td>
                      <td className="px-4 py-2 text-xs tabular-nums font-medium">{s.framedipaddress}</td>
                      <td className="px-4 py-2 text-[10px]">{s.acctstarttime || '-'}</td>
                      <td className="px-4 py-2 text-[10px] hidden md:table-cell">{s.acctstoptime || <span className="badge-completed md3-chip text-[9px]">Online</span>}</td>
                      <td className="px-4 py-2 text-[10px] text-right tabular-nums text-[var(--md-success)]">{formatBytes(s.acctinputoctets)}</td>
                      <td className="px-4 py-2 text-[10px] text-right tabular-nums text-[var(--md-primary)]">{formatBytes(s.acctoutputoctets)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        {/* Invoices */}
        <TabsContent value="invoices" className="mt-4">
          <div className="md3-card overflow-hidden">
            <table className="w-full">
              <thead><tr className="bg-[var(--md-surface-1)]">
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Invoice #</th>
                <th className="text-right text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Amount</th>
                <th className="text-right text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden md:table-cell">Tax</th>
                <th className="text-right text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Total</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Payment</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5">Status</th>
                <th className="text-left text-[10px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2.5 hidden md:table-cell">Issue Date</th>
              </tr></thead>
              <tbody>
                {(data.invoices || []).length === 0 ? <tr><td colSpan={7} className="text-center py-6 text-sm text-[var(--md-on-surface-variant)]">No invoices</td></tr> :
                (data.invoices || []).map((inv, i) => (
                  <tr key={i} className="border-t border-[var(--md-outline-variant)] md3-table-row">
                    <td className="px-4 py-2 text-sm font-medium tabular-nums text-[var(--md-primary)]">{inv.invnum}</td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums">{inv.price ? Number(inv.price).toLocaleString() : "-"}</td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums hidden md:table-cell">{inv.price && inv.issuedate && inv.issuedate >= "2026-05-04" ? Math.round(Number(inv.price) * 0.1).toLocaleString() : "0"}</td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums font-bold">{inv.price ? (inv.issuedate && inv.issuedate >= "2026-05-04" ? Math.round(Number(inv.price) * 1.1).toLocaleString() : Number(inv.price).toLocaleString()) + " MMK" : "-"}</td>
                    <td className="px-4 py-2 text-xs">{inv.paydate && inv.paydate !== "0000-00-00" ? inv.paydate : "-"}</td>
                    <td className="px-4 py-2"><span className={inv.paydate && inv.paydate !== "0000-00-00" ? "md3-chip text-[10px] badge-completed" : "md3-chip text-[10px] badge-pending"}>{inv.paydate && inv.paydate !== "0000-00-00" ? "Paid" : "Unpaid"}</span></td>
                    <td className="px-4 py-2 text-xs hidden md:table-cell">{inv.issuedate || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
